import { useEffect, useState } from 'react';

const EVENTS = ['pointermove', 'pointerdown', 'keydown', 'scroll', 'wheel', 'touchstart'] as const;

let interacted = false;
const listeners = new Set<() => void>();

function markInteracted() {
  if (interacted) return;
  interacted = true;
  for (const event of EVENTS) window.removeEventListener(event, markInteracted);
  listeners.forEach((notify) => notify());
  listeners.clear();
}

if (typeof window !== 'undefined') {
  for (const event of EVENTS) window.addEventListener(event, markInteracted, { passive: true });
}

/**
 * True once the visitor has moved the pointer, scrolled, touched, or pressed a key.
 * Used to defer heavy, purely decorative work (WebGL scenes) until after the page is interactive.
 */
export function useFirstInteraction(): boolean {
  const [ready, setReady] = useState(interacted);

  useEffect(() => {
    if (interacted) {
      setReady(true);
      return;
    }
    const notify = () => setReady(true);
    listeners.add(notify);
    return () => {
      listeners.delete(notify);
    };
  }, []);

  return ready;
}

/** Tracks whether an element is within `rootMargin` of the viewport. Attach the returned `ref` to the element. */
export function useInView<T extends Element>(rootMargin = '0px'): { ref: (el: T | null) => void; inView: boolean } {
  const [element, setElement] = useState<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!element || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { rootMargin });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, rootMargin]);

  return { ref: setElement, inView };
}
